import React, { useState } from 'react';
import { X, Wand2 } from 'lucide-react';
import { editImage } from '../../services/aiService';
import { ReferenceImageStrip } from './ReferenceImageStrip';

const EDIT_PROMPTS = {
    costume: (p) => `Edit this costume / outfit flat-lay image: ${p}. Keep everything else (layout, background, other garments and accessories) unchanged. Show only clothing and accessories — no person or mannequin.`,
    element: (p) => `Edit this prop / location / element image: ${p}. Keep everything else (composition, lighting, style, background) unchanged.`,
};

export const CostumeEditModal = ({ imageUrl, characterName, onApply, onClose, onTrackCreditsUsed, kind = 'costume', aspectRatio = '1:1' }) => {
    const label = kind === 'element' ? 'image' : 'costume';
    const [prompt, setPrompt] = useState('');
    const [refs, setRefs] = useState([]);
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [statusMsg, setStatusMsg] = useState('');
    const [error, setError] = useState(null);

    const handleEdit = async () => {
        if (!prompt.trim()) return;
        setBusy(true); setError(null); setStatusMsg(`Editing ${label}...`);
        try {
            const fullPrompt = EDIT_PROMPTS[kind](prompt.trim());
            const res = await editImage(result || imageUrl, fullPrompt, [], refs, null, 'default', aspectRatio, setStatusMsg);
            if (!res?.imageUrl) throw new Error('No edited image returned');
            setResult(res.imageUrl);
            if (onTrackCreditsUsed && res.creditsUsed) onTrackCreditsUsed(res.creditsUsed);
        } catch (e) { setError(e.message || 'Edit failed'); }
        finally { setBusy(false); setStatusMsg(''); }
    };

    const handleKeep = async () => {
        setBusy(true); setStatusMsg('Saving...');
        try { await onApply(result); } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={busy ? undefined : onClose} data-testid="costume-edit-modal-overlay">
            <div className="w-full max-w-3xl max-h-[92vh] overflow-y-auto bg-[#14141c] border border-white/10 rounded-2xl p-5" onClick={e => e.stopPropagation()} data-testid="costume-edit-modal">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-bold text-white">Edit {label}{characterName ? ` — ${characterName}` : ''}</h3>
                    <button type="button" onClick={onClose} disabled={busy} className="p-1.5 rounded-full hover:bg-white/10" data-testid="costume-edit-close"><X className="w-5 h-5" /></button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Original</p>
                        <img src={imageUrl} alt={`Original ${label}`} className="w-full aspect-square object-cover rounded-xl border border-white/10" crossOrigin="anonymous" referrerPolicy="no-referrer" data-testid="costume-edit-original" />
                    </div>
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Edited</p>
                        <div className="w-full aspect-square rounded-xl border border-dashed border-violet-500/30 bg-violet-500/[0.03] flex items-center justify-center overflow-hidden">
                            {busy && !result ? <span className="text-xs text-violet-200">{statusMsg || 'Working...'}</span>
                                : result ? <img src={result} alt={`Edited ${label}`} className="w-full h-full object-cover" data-testid="costume-edit-result" />
                                : <span className="text-xs text-zinc-500 px-4 text-center">Describe a change and click Edit</span>}
                        </div>
                    </div>
                </div>
                <textarea value={prompt} onChange={e => setPrompt(e.target.value)} rows={3} disabled={busy}
                    placeholder={kind === 'element' ? 'e.g. "make the walls light blue", "add a hanging lantern"' : 'e.g. "make the shirt dark red", "replace sandals with leather boots"'}
                    className="w-full mt-4 p-3 bg-white/5 border border-white/10 rounded-lg text-sm text-white resize-y" data-testid="costume-edit-prompt" />
                <ReferenceImageStrip images={refs} onChange={setRefs} testIdPrefix="costume-edit-refs" disabled={busy} />
                {error && <p className="text-xs text-red-400 mt-2" data-testid="costume-edit-error">{error}</p>}
                {busy && result && <p className="text-xs text-violet-200 mt-2">{statusMsg}</p>}
                <div className="flex justify-end gap-2 mt-4">
                    {result && (
                        <button type="button" onClick={() => setResult(null)} disabled={busy}
                            className="px-4 py-2 text-sm rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-200" data-testid="costume-edit-discard">Discard</button>
                    )}
                    <button type="button" onClick={handleEdit} disabled={busy || !prompt.trim()}
                        className="px-4 py-2 text-sm rounded-lg bg-violet-600 hover:bg-violet-500 disabled:bg-zinc-700 text-white font-semibold flex items-center gap-1.5" data-testid="costume-edit-submit">
                        <Wand2 className="w-4 h-4" /> {result ? 'Edit again' : 'Edit'}
                    </button>
                    {result && (
                        <button type="button" onClick={handleKeep} disabled={busy}
                            className="px-4 py-2 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold" data-testid="costume-edit-keep">Keep (replace original)</button>
                    )}
                </div>
            </div>
        </div>
    );
};
